import { useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

interface QueryParamHelpersReturn {
  getQueryParam: (key: string) => string;
  setQueryParam: (key: string, value: string) => void;
  removeQueryParam: (key: string) => void;
  setMultipleQueryParams: (params: Record<string, string>) => void;
  clearQueryParams: () => void;
}

export const useQueryParamHelpers = (): QueryParamHelpersReturn => {
  const { search, pathname } = useLocation();
  const history = useHistory();

  const queryParams = useMemo(() => new URLSearchParams(search), [search]);

  const getQueryParam = (key: string): string => {
    return queryParams.get(key) || '';
  };

  const setQueryParam = (key: string, value: string): void => {
    const params = new URLSearchParams(search);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    history.replace({
      pathname,
      search: params.toString(),
    });
  };

  const removeQueryParam = (key: string): void => {
    const params = new URLSearchParams(search);
    params.delete(key);
    history.replace({
      pathname,
      search: params.toString(),
    });
  };

  const setMultipleQueryParams = (newParams: Record<string, string>): void => {
    const params = new URLSearchParams(search);
    Object.entries(newParams).forEach(([key, value]) => {
      if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }
    });
    history.replace({
      pathname,
      search: params.toString(),
    });
  };

  const clearQueryParams = (): void => {
    history.replace({
      pathname,
    });
  };

  return {
    getQueryParam,
    setQueryParam,
    removeQueryParam,
    setMultipleQueryParams,
    clearQueryParams,
  };
};
